import { memo, useCallback, useEffect, useState } from 'react'

import { TextField, Paper, Typography, Box, Checkbox } from '@mui/material'
import { SmepEvent, SmepEventEdit, SmepEventInsert } from '@nativewaves/nw-cloud-sdk/smep'
import { isBefore, isEqual } from 'date-fns'
import { omit } from 'lodash-es'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'

import { Collapse } from '@shared/components/Collapse'
import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import {
  selectSubmissionProgressSetters,
  SubmissionButton,
  SubmissionProgress,
  useSubmissionProgressStore,
} from '@shared/components/SubmissionProgress'
import { DialogFormContent } from '@shared/layouts'
import { ControlledDateTimeRange } from 'domains/Workspaces/Events/ControlledDateTimeRange'
import { useEventCreateMutation, useEventUpdateMutation } from 'hooks/mutations/smep'
import { eventsRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events'

const timeDefaultValue = '0001-01-01T00:00:00.0000000+00:00' as const

export type FormStructure = SmepEventInsert | SmepEventEdit

type NewDialogProps = {
  onSuccess?: (data: SmepEvent) => void
  initialValue?: SmepEvent
}

const NewDialogRaw = ({ onSuccess, initialValue }: NewDialogProps) => {
  const { t } = useTranslation(['common', 'entity', 'domain'])
  const handleClose = useCloseHandler()

  const { workspaceId } = eventsRoute.useParams()

  const [manualChecked, setManualChecked] = useState(
    initialValue && initialValue.manualStartTime !== timeDefaultValue ? true : false,
  )
  const [referenceIdChecked, setReferenceIdChecked] = useState(initialValue?.referenceId ? true : false)

  const { setCurrentStep } = useSubmissionProgressStore(useShallow(selectSubmissionProgressSetters))

  const { register, handleSubmit, control, setError } = useForm<FormStructure>({
    reValidateMode: 'onSubmit',
    defaultValues: {
      workspaceId,
      ...initialValue,
    },
  })

  const { mutate: createEvent, ...createMutation } = useEventCreateMutation({})
  const { mutate: updateEvent, ...updateMutation } = useEventUpdateMutation()

  const handleFormSubmit = useCallback(
    (data: FormStructure) => {
      if (data.manualStartTime && data.manualEndTime) {
        if (isBefore(new Date(data.manualEndTime), new Date(data.manualStartTime))) {
          setError('manualEndTime', { message: "Manual end time can't be before manual start time" })

          return
        }

        if (isEqual(new Date(data.manualStartTime), new Date(data.manualEndTime))) {
          setError('manualStartTime', {
            message: 'Manual start time must at least be one minute prior to the manual end time',
          })
          setError('manualEndTime', {
            message: 'Manual end time must at least be one minute after the manual start time',
          })

          return
        }
      }

      if (manualChecked && !data.manualStartTime) {
        setError('manualStartTime', {
          message: 'At least a manual start time must be set if manual setup is selected (date & time)',
        })

        return
      }

      if (!initialValue) {
        const smepEventInsert = {
          ...omit(data, 'manualStartTime', 'manualEndTime', 'referenceId'),
          workspaceId,
          manualStartTime: manualChecked && data.manualStartTime ? data.manualStartTime : timeDefaultValue,
          manualEndTime: manualChecked && data.manualEndTime ? data.manualEndTime : undefined,
          referenceId: referenceIdChecked ? data.referenceId : null,
        } satisfies SmepEventInsert

        createEvent(
          { smepEventInsert },
          {
            onSuccess: (data) => {
              setTimeout(() => {
                handleClose()

                if (onSuccess) {
                  setTimeout(() => {
                    onSuccess(data)
                  }, 300)
                }
              }, 700)
            },
            onError: (_, variables) => {
              setCurrentStep({
                value: 100,
                description: `${variables.smepEventInsert.name} could not be created`,
              })
            },
          },
        )
      } else {
        const smepEventEdit = {
          ...data,
          manualStartTime: manualChecked ? data.manualStartTime : timeDefaultValue,
          manualEndTime: manualChecked ? data.manualEndTime : timeDefaultValue,
          referenceId: referenceIdChecked ? data.referenceId : null,
        } satisfies SmepEventEdit

        updateEvent(
          { eventId: initialValue.id, smepEventEdit },
          {
            onSuccess: (data) => {
              setTimeout(() => {
                handleClose()

                if (onSuccess) {
                  setTimeout(() => {
                    onSuccess(data)
                  }, 300)
                }
              }, 700)
            },
            onError: (_, variables) => {
              setCurrentStep({
                value: 100,
                description: `${variables.smepEventEdit.name} was not updated`,
              })
            },
          },
        )
      }
    },
    [
      createEvent,
      handleClose,
      initialValue,
      manualChecked,
      onSuccess,
      referenceIdChecked,
      setCurrentStep,
      setError,
      updateEvent,
      workspaceId,
    ],
  )

  useEffect(() => {
    register('manualStartTime')
    register('manualEndTime')
  }, [register])

  return (
    <DialogFormContent
      title={t('domain:Workspace.Event.createEvent')}
      onSubmit={handleSubmit(handleFormSubmit)}
      description={t('domain:Workspace.Event.createEventDescription')}
      SubmitProgressZone={
        <SubmissionProgress
          isSuccess={createMutation.isSuccess || updateMutation.isSuccess}
          isError={createMutation.isError || updateMutation.isError}
        />
      }
      SaveButton={
        <SubmissionButton
          submitCondition={!createMutation.isError || updateMutation.isError}
          successCondition={createMutation.isSuccess || updateMutation.isSuccess}
          errorCondition={createMutation.isError || updateMutation.isError}
          loading={createMutation.isPending || updateMutation.isPending}
          disabled={
            (!manualChecked && !referenceIdChecked) ||
            createMutation.isPending ||
            createMutation.isSuccess ||
            updateMutation.isPending ||
            updateMutation.isSuccess
          }
        />
      }
    >
      <Controller
        name="name"
        control={control}
        rules={{
          minLength: 3,
        }}
        render={({ field, fieldState: { invalid, error } }) => (
          <TextField
            sx={{ flex: 1 }}
            {...field}
            required
            margin="dense"
            label={t('name')}
            type="text"
            fullWidth
            variant="standard"
            autoComplete="off"
            error={invalid}
            helperText={error?.message}
          />
        )}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', rowGap: 1, mt: 3 }}>
        <Paper sx={{ width: '100%', bgcolor: 'background.levels.900', p: 1, mx: -1, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={[!manualChecked && { color: 'action.disabled' }]}>
              <Typography variant="heading-base">Manual Setup</Typography>
              <Typography variant="text">Set up an event with a start and end time.</Typography>
            </Box>
            <Checkbox checked={manualChecked} onChange={(_, value) => setManualChecked(value)} />
          </Box>

          <Collapse collapsed={!manualChecked}>
            <ControlledDateTimeRange control={control} />
          </Collapse>
        </Paper>
        <Paper
          sx={[
            { width: '100%', bgcolor: 'background.levels.900', p: 1, mx: -1 },
            !referenceIdChecked && { color: 'action.disabled' },
          ]}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="heading-base">Reference ID</Typography>
              <Typography variant="text">Use a reference ID to associate a specific event.</Typography>
            </Box>
            <Checkbox checked={referenceIdChecked} onChange={(_, value) => setReferenceIdChecked(value)} />
          </Box>

          <Controller
            name="referenceId"
            control={control}
            render={({ field, fieldState: { invalid, error } }) => (
              <TextField
                sx={{ flex: 1 }}
                {...field}
                margin="dense"
                placeholder={t('domain:Workspace.Event.referenceId')}
                type="text"
                fullWidth
                variant="outlined"
                autoComplete="off"
                error={invalid}
                helperText={error?.message}
                disabled={!referenceIdChecked}
              />
            )}
          />
        </Paper>
      </Box>
    </DialogFormContent>
  )
}

export const NewDialog = memo(NewDialogRaw)
